import { Lead, ContactAttempt, DeterminationEntry } from '../types/lead.types';
import { Appointment } from '../types/schedule.types';
import { format } from 'date-fns';

const CONTACT_METHOD_LABEL: Record<string, string> = {
  phone: 'Phone',
  email: 'Email',
  text: 'Text',
  'in-person': 'In Person',
};

const fmtTs = (ts: any): string =>
  ts?.toDate ? format(ts.toDate(), 'yyyy-MM-dd HH:mm') : '';

const sortContactsDesc = (attempts: ContactAttempt[]): ContactAttempt[] =>
  [...attempts].sort((a, b) => {
    const da = a.timestamp?.toDate?.()?.getTime?.() || 0;
    const dbb = b.timestamp?.toDate?.()?.getTime?.() || 0;
    return dbb - da;
  });

const sortDetsDesc = (entries: DeterminationEntry[]): DeterminationEntry[] =>
  [...entries].sort((a, b) => {
    const da = a.timestamp?.toDate?.()?.getTime?.() || 0;
    const dbb = b.timestamp?.toDate?.()?.getTime?.() || 0;
    return dbb - da;
  });

// Lead capture is rendered in the UI as the first "contact" even though it
// lives outside the contactAttempts array. Mirror that in the export so the
// total and history reflect what the user sees in the Contacts tab.
const summarizeContacts = (lead: Lead) => {
  const attempts = lead.contactAttempts || [];
  const sorted = sortContactsDesc(attempts);
  const last = sorted[0];
  const capturedAt = fmtTs(lead.createdAt);
  const capturedLine = capturedAt
    ? `${capturedAt} | In Person | Successful | by ${lead.capturedByName || lead.capturedBy || 'Unknown'} | Lead captured`
    : '';
  const attemptLines = sorted.map((c) =>
    [
      fmtTs(c.timestamp),
      CONTACT_METHOD_LABEL[c.method] || c.method,
      c.successful ? 'Successful' : 'Unsuccessful',
      `by ${c.userName || 'Unknown'}`,
      (c.notes || '').replace(/\n/g, ' ').trim(),
    ].join(' | ')
  );
  return {
    totalContacts: attempts.length + (capturedAt ? 1 : 0),
    lastContactDate: last ? fmtTs(last.timestamp) : capturedAt,
    lastContactMethod: last
      ? CONTACT_METHOD_LABEL[last.method] || last.method
      : capturedAt
        ? 'In Person'
        : '',
    lastContactSuccessful: last
      ? last.successful
        ? 'Yes'
        : 'No'
      : capturedAt
        ? 'Yes'
        : '',
    lastContactNotes: last?.notes || '',
    contactHistory: [...attemptLines, capturedLine].filter(Boolean).join(' || '),
  };
};

const summarizeDetermination = (lead: Lead) => {
  const history = lead.determinationHistory || [];
  const sorted = sortDetsDesc(history);
  const latest = sorted[0];
  const lines = sorted.map((e) =>
    [
      fmtTs(e.timestamp),
      e.determination,
      `by ${e.userName || 'Unknown'}`,
      (e.notes || '').replace(/\n/g, ' ').trim(),
    ].join(' | ')
  );
  return {
    determinationUpdatedAt: latest ? fmtTs(latest.timestamp) : '',
    determinationUpdatedBy: latest?.userName || '',
    determinationNotes: latest?.notes || '',
    determinationChanges: history.length,
    determinationHistory: lines.join(' || '),
  };
};

const summarizeAppointments = (appts: Appointment[]) => {
  if (!appts.length) {
    return {
      appointmentCount: 0,
      nextAppointment: '',
      lastAppointment: '',
      appointmentHistory: '',
    };
  }
  // Sort chronologically (date + startTime) ascending.
  const sorted = [...appts].sort((a, b) => {
    const ak = `${a.date} ${a.startTime}`;
    const bk = `${b.date} ${b.startTime}`;
    return ak.localeCompare(bk);
  });
  const todayKey = format(new Date(), 'yyyy-MM-dd HH:mm');
  const upcoming = sorted.find((a) => `${a.date} ${a.startTime}` >= todayKey && a.status === 'scheduled');
  const past = [...sorted].reverse().find((a) => `${a.date} ${a.startTime}` < todayKey);
  const fmtApt = (a: Appointment) =>
    `${a.date} ${a.startTime}-${a.endTime} | ${a.eventType} | ${a.status}`;
  return {
    appointmentCount: appts.length,
    nextAppointment: upcoming ? fmtApt(upcoming) : '',
    lastAppointment: past ? fmtApt(past) : '',
    appointmentHistory: sorted.map(fmtApt).join(' || '),
  };
};

export const exportLeadsToCSV = (
  leads: Lead[],
  filename: string = 'leads-export',
  appointmentsByLeadId: Record<string, Appointment[]> = {}
) => {
  const headers = [
    'Lead ID',
    'Parent Name',
    'Email',
    'Phone',
    'Address',
    'Status',
    'Determination',
    'Children Count',
    'Children Names',
    'Captured By',
    'Captured Date',
    'Last Updated',
    'Notes',
    // Contact history
    'Total Contacts',
    'Last Contact Date',
    'Last Contact Method',
    'Last Contact Successful',
    'Last Contact Notes',
    'Contact History',
    // Determination history
    'Determination Updated At',
    'Determination Updated By',
    'Determination Notes',
    'Determination Changes',
    'Determination History',
    // Schedule / appointments
    'Appointment Count',
    'Next Appointment',
    'Last Appointment',
    'Appointment History',
  ];

  const rows = leads.map(lead => {
    const childrenNames = lead.children
      .map(child => `${child.firstName || child.name} ${child.lastName || ''}`.trim())
      .join('; ');

    const contact = summarizeContacts(lead);
    const det = summarizeDetermination(lead);
    const appts = summarizeAppointments(appointmentsByLeadId[lead.id] || []);

    return [
      lead.id,
      lead.parentName,
      lead.email,
      lead.phone,
      lead.address,
      lead.status,
      lead.determination || 'pending',
      lead.children.length,
      childrenNames,
      lead.capturedByName || lead.capturedBy || '',
      lead.createdAt ? format(lead.createdAt.toDate(), 'yyyy-MM-dd HH:mm') : '',
      lead.lastUpdated ? format(lead.lastUpdated.toDate(), 'yyyy-MM-dd HH:mm') : '',
      lead.notes || '',
      contact.totalContacts,
      contact.lastContactDate,
      contact.lastContactMethod,
      contact.lastContactSuccessful,
      contact.lastContactNotes,
      contact.contactHistory,
      det.determinationUpdatedAt,
      det.determinationUpdatedBy,
      det.determinationNotes,
      det.determinationChanges,
      det.determinationHistory,
      appts.appointmentCount,
      appts.nextAppointment,
      appts.lastAppointment,
      appts.appointmentHistory,
    ];
  });

  const csvContent = [
    headers.join(','),
    ...rows.map(row =>
      row.map(cell => {
        const cellStr = String(cell ?? '');
        if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
          return `"${cellStr.replace(/"/g, '""')}"`;
        }
        return cellStr;
      }).join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};
